/**
 * Gallery cleanup utilities for managing expired galleries
 * Provides safe, manual cleanup options with admin oversight
 */

import { supabase } from './supabase';
import { deleteFromS3 } from './serverlessS3Service';

export interface ExpiredGallery {
  id: string;
  token: string;
  title: string;
  client_name: string;
  client_email?: string;
  expires_at: string;
  created_at: string;
  image_count: number;
  days_expired: number;
}

export interface CleanupResult {
  success: boolean;
  deletedImages: number;
  error?: string;
}

/**
 * Get all expired galleries with details
 */
export const getExpiredGalleries = async (): Promise<ExpiredGallery[]> => {
  try {
    const { data, error } = await supabase
      .from('client_galleries')
      .select(`
        id,
        token,
        title,
        client_name,
        client_email,
        expires_at,
        created_at,
        gallery_images(id)
      `)
      .not('expires_at', 'is', null)
      .lt('expires_at', new Date().toISOString())
      .eq('is_active', true);

    if (error) throw error;

    return data?.map(gallery => ({
      id: gallery.id,
      token: gallery.token,
      title: gallery.title,
      client_name: gallery.client_name,
      client_email: gallery.client_email,
      expires_at: gallery.expires_at,
      created_at: gallery.created_at,
      image_count: gallery.gallery_images?.length || 0,
      days_expired: Math.floor(
        (Date.now() - new Date(gallery.expires_at).getTime()) / (1000 * 60 * 60 * 24)
      )
    })) || [];
  } catch (error) {
    console.error('Error fetching expired galleries:', error);
    return [];
  }
};

/**
 * Get galleries expiring soon (within specified days)
 */
export const getGalleriesExpiringSoon = async (days: number = 7): Promise<ExpiredGallery[]> => {
  try {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    const { data, error } = await supabase
      .from('client_galleries')
      .select(`
        id,
        token,
        title,
        client_name,
        client_email,
        expires_at,
        created_at,
        gallery_images(id)
      `)
      .not('expires_at', 'is', null)
      .gt('expires_at', new Date().toISOString())
      .lt('expires_at', futureDate.toISOString())
      .eq('is_active', true);

    if (error) throw error;

    return data?.map(gallery => ({
      id: gallery.id,
      token: gallery.token,
      title: gallery.title,
      client_name: gallery.client_name,
      client_email: gallery.client_email,
      expires_at: gallery.expires_at,
      created_at: gallery.created_at,
      image_count: gallery.gallery_images?.length || 0,
      days_expired: Math.ceil(
        (new Date(gallery.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
    })) || [];
  } catch (error) {
    console.error('Error fetching galleries expiring soon:', error);
    return [];
  }
};

/**
 * Archive expired gallery (disable instead of delete)
 */
export const archiveExpiredGallery = async (galleryId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('client_galleries')
      .update({ is_active: false })
      .eq('id', galleryId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error archiving gallery:', error);
    return false;
  }
};

/**
 * Extend gallery expiration
 */
export const extendGalleryExpiration = async (
  galleryId: string, 
  additionalDays: number
): Promise<boolean> => {
  try {
    // Get current expiration date
    const { data: gallery, error: fetchError } = await supabase
      .from('client_galleries')
      .select('expires_at')
      .eq('id', galleryId)
      .single();

    if (fetchError) throw fetchError;

    // Calculate new expiration date
    const currentExpiry = gallery.expires_at ? new Date(gallery.expires_at) : new Date();
    const newExpiry = new Date(currentExpiry.getTime() + (additionalDays * 24 * 60 * 60 * 1000));

    // Update expiration date
    const { error: updateError } = await supabase
      .from('client_galleries')
      .update({ expires_at: newExpiry.toISOString() })
      .eq('id', galleryId);

    if (updateError) throw updateError;
    return true;
  } catch (error) {
    console.error('Error extending gallery expiration:', error);
    return false;
  }
};

/**
 * DANGEROUS: Permanently delete gallery files from S3
 * Use with extreme caution - this cannot be undone
 */
export const permanentlyDeleteGalleryFiles = async (galleryId: string): Promise<CleanupResult> => {
  try {
    // Get all images for this gallery
    const { data: images, error: fetchError } = await supabase
      .from('gallery_images')
      .select('filename, original_storage_provider')
      .eq('gallery_id', galleryId);

    if (fetchError) throw fetchError;

    if (!images || images.length === 0) {
      return { success: true, deletedImages: 0 };
    }

    let deletedCount = 0;
    const deletePromises = images.map(async (image) => {
      try {
        // Delete both preview and original from S3
        const previewKey = `previews/${image.filename}`;
        const originalKey = `originals/${image.filename.replace('_preview.jpg', '_original.')}`;
        
        await deleteFromS3(previewKey);
        
        // Try multiple extensions for original
        const extensions = ['jpg', 'jpeg', 'png', 'webp', 'tiff', 'raw'];
        for (const ext of extensions) {
          await deleteFromS3(originalKey + ext);
        }
        
        deletedCount++;
      } catch (error) {
        console.warn(`Failed to delete files for ${image.filename}:`, error);
      }
    });

    await Promise.all(deletePromises);

    return { success: true, deletedImages: deletedCount };
  } catch (error) {
    console.error('Error deleting gallery files:', error);
    return { 
      success: false, 
      deletedImages: 0,
      error: error instanceof Error ? error.message : 'Failed to delete files'
    };
  }
};

/**
 * Get storage usage statistics
 */
export const getStorageStats = async () => {
  try {
    const { data: stats, error } = await supabase
      .from('gallery_images')
      .select(`
        original_file_size,
        gallery_id,
        client_galleries(is_active, expires_at)
      `);

    if (error) throw error;

    const now = new Date();
    let activeSize = 0;
    let expiredSize = 0;
    let expiredCount = 0;
    let activeCount = 0;

    stats?.forEach((image: any) => {
      const gallery = image.client_galleries;
      const size = image.original_file_size || 0;
      
      if (!gallery?.is_active || (gallery?.expires_at && new Date(gallery.expires_at) < now)) {
        expiredSize += size;
        expiredCount++;
      } else {
        activeSize += size;
        activeCount++;
      }
    });

    return {
      active: { size: activeSize, count: activeCount },
      expired: { size: expiredSize, count: expiredCount },
      total: { size: activeSize + expiredSize, count: activeCount + expiredCount }
    };
  } catch (error) {
    console.error('Error calculating storage stats:', error);
    return null;
  }
};

/**
 * Format bytes to human readable string
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};